/**
 * The Transparent conversion — ONE implementation, TWO callers (WS-N / WS-J deferred 1).
 *
 * This file is a MOVE, not a rewrite. Everything here was the body of `TransparentPanel`'s
 * `pickAndRun` and `saveAll`; the only substitutions are the ones the extraction requires:
 * `setRows`/`setProgress` became `hooks.onRow`, and `cancelled.current` became
 * `hooks.shouldAbort()`. Byte-identity with the pre-extraction panel is therefore a property of
 * the code's SHAPE rather than of a test's goodwill — the panel and the queue arm call the same
 * function, so there is no second implementation that could drift (the WS-H2 H2-1 pattern).
 *
 * The keyer itself lives in `keying.ts` and is NOT touched by this wave: `keying.test.ts`,
 * `keyer-parity.test.ts` and `scripts/check_overlays.py`'s 53/53 stay unedited, which is exactly
 * what makes them evidence for this extraction instead of decoration.
 */
import type { PickedFile, PlatformIO } from "@genz/platform";
import {
  destLabel, exportBundle, exportProblemSummary, openFailureSuffix, TRANSPARENT_BUNDLE_ROOT,
  type ExportReport,
} from "../export/bundle.js";
import {
  keyImage, normalizePlacement,
  type KeyingConfig, type LayoutConfig,
} from "./keying.js";
// The DOM-free leaf `queue/manager.ts` also imports, so a file is keyed and reported by ONE
// implementation of its relative path.
import { IMAGE_RE, relPathOf } from "./paths.js";

export { IMAGE_RE, relPathOf };

/** The logging surface this module needs — the `AppLogger` subset, so a caller can inject one. */
export interface TransparentLogger {
  log(level: string, message: string, ctx?: Record<string, unknown>): void;
}

/** One converted file. Moved verbatim from `TransparentPanel`'s private `Row`. */
export interface TransparentRow {
  /** The source file's relative path — the row's identity across pause, resume and retry. */
  source: string;
  /** Where the row is filed: an ok row's `.png` output, otherwise the source path (AR-11). */
  relPath: string;
  status: "ok" | "skip" | "fail";
  note?: string;
  roundtrip?: number;
  panelShare?: number;
  clearShare?: number;
  bytes?: Uint8Array;
}

/** Everything one conversion needs, and nothing about who asked for it. */
export interface TransparentRunSpec {
  /** Already filtered to images by the caller (see `IMAGE_RE`). */
  files: PickedFile[];
  keying: KeyingConfig;
  layout: LayoutConfig;
  /** Top-level output folder for this run's brand; `""` keeps the source layout. */
  brandFolder: string;
  /** WS-I / D-13: stamped when the run STARTS, so one conversion writes one folder. */
  runFolder?: string;
  destDir?: string;
}

/** What the caller wants to know while the loop runs, and how it says "stop". */
export interface TransparentRunHooks {
  /**
   * One row is done. May return a promise — WS-O / D-36 hangs the write-through writer off
   * this seam, and the loop AWAITS it for the same reason `runBatch` awaits `onItem`: an
   * un-awaited sink lets the conversion out-run the disk and holds the whole run in the heap.
   * A caller that returns nothing (the panel's `setRows`) is unaffected.
   */
  onRow(i: number, n: number, row: TransparentRow): void | Promise<void>;
  /** A non-null reason stops the loop BETWEEN files, keeping everything already converted. */
  shouldAbort(): string | null;
}


/** Draw keyed RGBA onto the square canvas per the placement spec and encode as PNG. */
export async function encodeNormalized(
  out: Uint8ClampedArray, w: number, h: number, layout: LayoutConfig,
): Promise<Uint8Array> {
  const place = normalizePlacement(out, w, h, layout);
  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = w; srcCanvas.height = h;
  // Copy: ImageData's lib type wants a plain Uint8ClampedArray backed by ArrayBuffer.
  const px = new ImageData(w, h);
  px.data.set(out);
  srcCanvas.getContext("2d")!.putImageData(px, 0, 0);
  const dst = document.createElement("canvas");
  dst.width = layout.canvas; dst.height = layout.canvas;
  const ctx = dst.getContext("2d")!;
  ctx.fillStyle = `rgb(${place.back[0]},${place.back[1]},${place.back[2]})`;
  ctx.fillRect(0, 0, layout.canvas, layout.canvas);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  const dw = Math.round(w * place.scale), dh = Math.round(h * place.scale);
  // The backdrop fill must stop at the case rect: drawImage composites source-over, so
  // without this the keyed panel's a=0 pixels come out as the opaque fill colour. The batch
  // twin pastes without a mask (replace semantics) — clearing first is the canvas equivalent.
  ctx.clearRect(place.dx, place.dy, dw, dh);
  ctx.drawImage(srcCanvas, place.dx, place.dy, dw, dh);
  const blob = await new Promise<Blob | null>((res) => dst.toBlob(res, "image/png"));
  if (!blob) throw new Error("PNG encode failed");
  return new Uint8Array(await blob.arrayBuffer());
}

/** Folder-safe brand segment: printable, no path separators, trimmed. */
export function brandFolder(name: string): string {
  return name.trim().replace(/[/\\:*?"<>|]+/g, "-").replace(/\s+/g, " ").slice(0, 60);
}

/**
 * Convert every file in `spec`, in order. The loop is `pickAndRun`'s, verbatim.
 *
 * Returns what it managed to convert: an abort between files keeps the rows already produced
 * (AUD-3 §5.10 — cancellation is real and outputs are kept), and a file that throws is a `fail`
 * ROW with the reason, never a silent skip (§5.9).
 */
export async function convertTransparentFiles(
  io: Pick<PlatformIO, "decodeImage">,
  logger: TransparentLogger | undefined,
  spec: TransparentRunSpec,
  hooks: TransparentRunHooks,
): Promise<TransparentRow[]> {
  const images = spec.files;
  logger?.log(
    "info",
    `Transparent · converting ${images.length} file${images.length === 1 ? "" : "s"} → ` +
      `${TRANSPARENT_BUNDLE_ROOT}${spec.runFolder ? `/${spec.runFolder}` : ""}`,
    { count: images.length, runFolder: spec.runFolder },
  );
  const done: TransparentRow[] = [];
  for (let i = 0; i < images.length; i++) {
    const abort = hooks.shouldAbort();
    if (abort) {
      logger?.log(
        "info",
        `Transparent · stopped after ${done.length} of ${images.length} — ${abort}`,
        { converted: done.length, total: images.length, reason: abort },
      );
      break;
    }
    const f = images[i];
    const relPath = relPathOf(f);
    try {
      const img = await io.decodeImage(f.bytes);
      const res = keyImage(new Uint8ClampedArray(img.rgba.buffer, img.rgba.byteOffset, img.rgba.length),
        img.width, img.height, spec.keying);
      if (!res) {
        done.push({ source: relPath, relPath, status: "skip", note: "no print guide panel" });
        // IM-4: this outcome used to exist ONLY as a row in a table that disappears on tab
        // switch — Settings → Observability → Error log never heard about it.
        logger?.log("warn", `Transparent · ${relPath} — no magenta panel`, { relPath });
      } else {
        const bytes = await encodeNormalized(res.out, img.width, img.height, spec.layout);
        done.push({
          source: relPath,
          relPath: relPath.replace(/\.[^.]+$/, ".png"),
          status: "ok",
          roundtrip: res.roundtrip,
          panelShare: res.panelShare,
          clearShare: res.clearShare,
          bytes,
        });
      }
    } catch (e) {
      const note = e instanceof Error ? e.message : String(e);
      done.push({ source: relPath, relPath, status: "fail", note });
      logger?.log("error", `Transparent · ${relPath} — ${note}`, { relPath });
    }
    await hooks.onRow(i + 1, images.length, done[done.length - 1]);
    // Yield so the progress line paints between files.
    await new Promise((r) => setTimeout(r, 0));
  }
  const counts = {
    ok: done.filter((r) => r.status === "ok").length,
    skipped: done.filter((r) => r.status === "skip").length,
    failed: done.filter((r) => r.status === "fail").length,
  };
  logger?.log(
    "info",
    `Transparent · converted ${counts.ok} ok, ${counts.skipped} skipped, ${counts.failed} failed`,
    counts,
  );
  return done;
}

/**
 * WS-I / D-11 (AUD-3 IM-4 / F-47): the overlays go through the SHARED export bundle, not a
 * private `io.exportBundle` call with its own folder name. That buys the case-insensitive
 * collision report, per-file failure reasons, the cancelled-dialog distinction, the run
 * folder, the remembered save folder and `destPath` — all of which this surface simply did
 * not have. `mime` is gone: `sniffImageExt` reads the real format from the magic bytes.
 *
 * WS-O / O-10-11: this is the WEB save now, and only that — the Transparent tab's
 * "Download (N files)" click, which is a zip a browser cannot be handed without a gesture
 * (WS-N review-1 M-4). On desktop the conversion writes each overlay as it keys it, through
 * `export/runWriter.ts`, so there is no end-of-job save and no "Save all" to re-file one.
 * Review-1 M-5's `claimed` parameter went with them: the writer owns the run's claim map and
 * hands it to every one of its per-overlay calls, and a one-shot zip has no segments to span.
 */
export async function saveTransparentRows(
  io: PlatformIO,
  logger: TransparentLogger | undefined,
  rows: TransparentRow[],
  spec: Pick<TransparentRunSpec, "brandFolder" | "runFolder" | "destDir">,
): Promise<ExportReport | null> {
  const folder = spec.brandFolder;
  const files = rows
    .filter((r): r is TransparentRow & { bytes: Uint8Array } => r.status === "ok" && !!r.bytes)
    .map((r) => ({
      // A chosen brand becomes the top-level folder; the source's own subfolders are
      // kept beneath it so nothing collides between brands.
      relPath: folder ? `${folder}/${r.relPath}` : r.relPath,
      bytes: r.bytes,
    }));
  // `null` = there was nothing to save. Distinct from `report.cancelled` (the user declined the
  // dialog) and from a report with failures — the caller phrases all three differently.
  if (files.length === 0) return null;
  return exportBundle(io, {
    name: TRANSPARENT_BUNDLE_ROOT,
    files,
    destDir: spec.destDir,
    runFolder: spec.runFolder,
    logger,
  });
}

/**
 * WS-N review-1 M-4: does a FINISHED conversion file itself, or wait for the operator's click?
 *
 * Desktop writes a folder tree, and saving on completion is the whole point of making the
 * conversion a queue job — it produces files with nobody watching the tab. On WEB the same call
 * is a zip DOWNLOAD (`platform/src/web.ts` builds a blob and calls `a.click()`), so firing it
 * from job completion is a download with no user gesture behind it: Chrome allows programmatic
 * anchor downloads, Safari's behaviour without a gesture is not guaranteed, and before WS-N it
 * rode on the operator's own "Save all" click. On web the tab offers "Download (N files)"
 * instead, and this is the ONE place the two callers agree on which it is.
 *
 * Review-2 nit: the parameter is `PlatformIO["kind"]`, not `string`. Every value that is not
 * `"web"` takes the desktop branch, so a typo'd kind used to compile into a gesture-less
 * download — the one outcome this function exists to prevent. The union makes it a build error.
 */
export function savesWhenTheJobFinishes(ioKind: PlatformIO["kind"]): boolean {
  return ioKind !== "web";
}

/** The operator sentence for a finished save. Moved verbatim from `saveAll`'s `setSaveMsg`. */
export function savedMessage(report: ExportReport, ioKind: string, savedDir: string | undefined): string {
  return (
    `Saved ${report.written} overlay${report.written === 1 ? "" : "s"} ` +
    `${destLabel(report, ioKind, savedDir)}.` +
    `${exportProblemSummary(report)}${openFailureSuffix(report)}`
  );
}
